import { gql, useMutation } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import cx from 'classnames'
import NewMultiUseDiscountCode from 'components/Dashboard/NewMultiUseDiscountCode'
import SkuSelectInput from 'components/Dashboard/SkuSelectInput'
import GenericServerError from 'components/GenericServerError'
import SubmitButton from 'components/SubmitButton'
import TextInput from 'components/TextInput'
import { Formik } from 'formik'
import { Link, navigate } from 'gatsby'
import { useEffect, useState } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'
import * as yup from 'yup'

const GENERATE_IDS_MUTATION = gql`
  mutation GenerateDiscountCodes($quantity: Int!, $batchName: String!, $thirdPartyAssociateId: ID, $sku: String!) {
    generateDiscountCodes(
      input: { quantity: $quantity, batchName: $batchName, thirdPartyAssociateId: $thirdPartyAssociateId, sku: $sku }
    ) {
      codes
    }
  }
`

const validationSchema = yup.object().shape({
  quantity: yup.number().max(200).required(),
  batchName: yup.string().required(),
  thirdPartyAssociateId: yup.string(),
})

const GenerateBulkDiscountCodes = () => {
  const [thirdPartyId] = useQueryParam('thirdPartyId', StringParam)

  const [generate, { loading, error, data }] = useMutation(GENERATE_IDS_MUTATION)

  const initialValues = {
    quantity: 10,
    batchName: '',
    thirdPartyAssociateId: thirdPartyId,
    sku: 'ncov',
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        await generate({ variables: values })
      }}
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <TextInput name="quantity" label="Quantity" type="number" />

          <TextInput name="batchName" label="Batch name" />

          <TextInput name="thirdPartyAssociateId" label="Third party code" />

          <SkuSelectInput name="sku" />

          {error && <GenericServerError />}

          <div className="field" style={{ paddingTop: 18 }}>
            <SubmitButton className="is-midnightBlue is-rounded" submitting={loading}>
              Generate Discount Codes
            </SubmitButton>
          </div>

          {data?.generateDiscountCodes?.codes && <pre>{data.generateDiscountCodes.codes.join('\n')}</pre>}
        </form>
      )}
    </Formik>
  )
}

const GenerateDiscountCodes = ({ location }: RouteComponentProps) => {
  const [activeTab, setActiveTab] = useState<number>()
  const [thirdPartyId, setThirdPartyId] = useState<string>()

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    setThirdPartyId(params.get('thirdPartyId'))
    setActiveTab(Number(params.get('tab') || 0))
  }, [location.search])

  return (
    <>
      <nav className="breadcrumb ml-6" aria-label="breadcrumbs">
        <ul>
          <li>
            <Link to="/dashboard/third-parties">Third Parties</Link>
          </li>
          <li>
            <a href={`/dashboard/third-party/${thirdPartyId}`}>{thirdPartyId}</a>
          </li>
          <li className="is-active">
            <a href={`/dashboard/generate-discount-codes?thirdPartyId=${thirdPartyId}`} aria-current="page">
              Discount Codes
            </a>
          </li>
        </ul>
      </nav>

      <section className="section is-small">
        <div className="container">
          <h1 className="title">Generate Discount Codes</h1>
          <div className="tabs">
            <ul>
              <li className={cx({ 'is-active': activeTab === 0 })}>
                <a
                  href={`/dashboard/generate-discount-codes?thirdPartyId=${thirdPartyId}`}
                  onClick={(e) => {
                    // TODO: Change query param in URL
                    e.preventDefault()
                    setActiveTab(0)
                  }}
                >
                  Bulk single-use codes
                </a>
              </li>

              <li className={cx({ 'is-active': activeTab === 1 })}>
                <a
                  href={`/dashboard/generate-discount-codes?thirdPartyId=${thirdPartyId}`}
                  onClick={(e) => {
                    // TODO: Change query param in URL
                    e.preventDefault()
                    setActiveTab(1)
                  }}
                >
                  Multi-use code
                </a>
              </li>
            </ul>
          </div>

          {activeTab === 0 && <GenerateBulkDiscountCodes />}
          {activeTab === 1 && (
            <NewMultiUseDiscountCode
              thirdPartyId={thirdPartyId}
              onSuccess={(discountCode) =>
                navigate(`/dashboard/third-party/${thirdPartyId}/discount-code/${discountCode}`)
              }
            />
          )}
        </div>
      </section>
    </>
  )
}
export default GenerateDiscountCodes
